import { Button } from '@/components/ui/shadcn/button';
import { DataTable } from '@/components/ui/shadcn/data-table';
import { formatCurrency, formatDate } from '@/lib/utils';
import { ColumnDef } from '@tanstack/react-table';
import { Edit, Lock, Trash2 } from 'lucide-react';
import { areCostsLocked, CostLine, EntityType, EntityWithCosts } from './utils';

type TranslationFunction = (key: string, fallback?: string) => string;

interface CostsTableProps {
    entity: {
        id: number | string;
        cost_lines?: CostLine[];
        project?: { id: number | string } | null;
        status?: string;
    };
    onEdit?: (cost: CostLine) => void;
    onDelete?: (cost: CostLine) => void;
    entityType?: EntityType;
    canManage?: boolean;
    t?: TranslationFunction;
}

const getTypeLabel = (type: string, t: TranslationFunction = (k, f) => f || k) => {
    const typeKey = `common.costs.type.${type}`;
    const fallback = type
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    return t(typeKey, fallback);
};

export function CostsTable({ entity, onEdit, onDelete, entityType, canManage = true, t = (k, f) => f || k }: CostsTableProps) {
    const costs = entity.cost_lines || [];
    const locked =
        !canManage ||
        areCostsLocked(
            entity as EntityWithCosts & {
                project?: { id: number | string } | null;
                status?: string;
            },
            entityType,
        );

    const columns: ColumnDef<CostLine>[] = [
        {
            accessorKey: 'description',
            header: t('common.costs.table.description', 'Description'),
            cell: ({ row }) => {
                const cost = row.original;
                if (locked || !onEdit) {
                    return <span className="font-medium">{cost.description}</span>;
                }
                return (
                    <Button variant="link" className="h-auto justify-start p-0 text-left font-medium hover:underline" onClick={() => onEdit(cost)}>
                        {cost.description}
                    </Button>
                );
            },
        },
        {
            accessorKey: 'type',
            header: t('common.costs.table.type', 'Type'),
            cell: ({ row }) => {
                const type = row.original.type;
                return <span className="bg-muted rounded-md px-2 py-1 text-sm">{getTypeLabel(type, t)}</span>;
            },
        },
        {
            accessorKey: 'amount',
            header: t('common.costs.table.amount', 'Amount'),
            cell: ({ row }) => {
                const amount = row.original.amount;
                return formatCurrency(Number(amount));
            },
        },
        {
            accessorKey: 'date_incurred',
            header: t('common.costs.table.date', 'Date'),
            cell: ({ row }) => {
                const date = row.original.date_incurred;
                return date ? formatDate(date.toString()) : '-';
            },
        },
        {
            accessorKey: 'created_by',
            header: t('common.costs.table.added_by', 'Added By'),
            cell: ({ row }) => {
                const createdBy = row.original.created_by;
                if (!createdBy || typeof createdBy !== 'object') return <span className="text-muted-foreground">-</span>;
                return (
                    <span className="text-sm">
                        {createdBy.first_name} {createdBy.last_name}
                    </span>
                );
            },
        },
    ];

    // Only add actions column if costs are not locked and handlers are provided
    if (!locked && (onEdit || onDelete)) {
        columns.push({
            id: 'actions',
            header: t('common.costs.table.actions', 'Actions'),
            cell: ({ row }) => {
                const cost = row.original;
                return (
                    <div className="flex items-center gap-2">
                        {onEdit && (
                            <Button variant="ghost" size="sm" onClick={() => onEdit(cost)} className="h-8 w-8 p-0">
                                <Edit className="h-4 w-4" />
                            </Button>
                        )}
                        {onDelete && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => onDelete(cost)}
                                className="text-destructive hover:text-destructive/90 h-8 w-8 p-0"
                            >
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        )}
                    </div>
                );
            },
        });
    } else {
        columns.push({
            id: 'actions',
            header: t('common.costs.table.actions', 'Actions'),
            cell: () => {
                return (
                    <span className="text-muted-foreground">
                        <Lock className="h-4 w-4" />
                    </span>
                );
            },
        });
    }

    if (costs.length === 0) {
        return <div className="text-muted-foreground py-8 text-center">{t('common.costs.table.no_costs', 'No costs added yet')}</div>;
    }

    return (
        <DataTable
            columns={columns}
            data={costs}
            search={{ columnId: 'description', placeholder: t('common.costs.table.search', 'Search by description...') }}
        />
    );
}
